import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Camera } from 'lucide-react';
import { parseWeighingDocument } from '../../api/ocr';
import { useMasterData } from '../../hooks/useMasterData';
import { Button } from '../../components/ui/Button';
import { Card, CardBody } from '../../components/ui/Card';
import { PageHeader } from '../../components/ui/PageHeader';
import { Spinner } from '../../components/ui/Spinner';
import { ApiRequestError } from '../../api/client';
import { DocumentScannerModal, type DocumentScannerHandle } from '../scanner/DocumentScannerModal';
import type { WeighingFormPrefill } from '../weighings/WeighingFormPage';

/**
 * Nachfolger von `ocr_entry.php`/`ocr_entry_w3.php`: Scan hochladen, Google
 * Vision liest den Text - und statt eines Zwischenschritts mit
 * "Übernehmen & prüfen"-Button geht es direkt weiter zum normalen
 * Erfassungsformular (vorbefüllt), das die Person vor dem Speichern ohnehin
 * prüft/korrigiert. Übernommen wird NIE automatisch gespeichert.
 */
export function OcrCapturePage() {
  const navigate = useNavigate();
  const { plants, isLoading: masterDataLoading } = useMasterData();
  const [plantCode, setPlantCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scannerRef = useRef<DocumentScannerHandle>(null);

  async function handleFile(file: File) {
    setError(null);
    setLoading(true);
    try {
      const res = await parseWeighingDocument(file, plantCode);
      const plant = plants.data?.find((p) => p.code === plantCode);
      const prefill: WeighingFormPrefill = {
        plantId: plant?.id ?? null,
        dateTime: res.suggested.dateTime,
        gateNumber: res.suggested.gateNumber,
        licensePlate: res.suggested.licensePlate,
        containerId: res.suggested.containerId,
        containerNameRaw: res.suggested.containerNameRaw,
        materialId: res.suggested.materialId,
        alloyId: res.suggested.alloyId,
        driverId: res.suggested.driverId,
        netWeightKg: res.suggested.netWeightKg,
        remarks: res.suggested.remarks,
        documentId: res.documentId,
      };
      navigate('/weighings/new', { state: { prefill, source: 'OCR' } });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'OCR-Erkennung fehlgeschlagen. Bitte manuell erfassen.');
    } finally {
      setLoading(false);
    }
  }

  if (masterDataLoading) return <Spinner />;

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <PageHeader title="OCR-Erfassung" description="Wiegeschein fotografieren oder hochladen - die Felder werden automatisch vorgeschlagen." />

      <Card>
        <CardBody className="flex flex-col gap-4">
          <div>
            <span className="mb-1.5 block text-sm font-medium text-ivory-300">Werk</span>
            <div className="grid grid-cols-2 gap-2">
              {plants.data?.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPlantCode(p.code)}
                  className={clsx(
                    'rounded-md border px-4 py-3 text-sm font-medium transition-colors',
                    plantCode === p.code
                      ? 'border-violet-500 bg-violet-500/15 text-violet-300'
                      : 'border-surface-600 bg-surface-800 text-ivory-300 hover:border-surface-500 hover:bg-surface-700',
                  )}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col items-center gap-3 rounded-md border-2 border-dashed border-surface-600 p-10 text-center text-sm">
            {loading ? (
              <>
                <Spinner />
                <p className="text-xs text-ivory-500">Wird erkannt…</p>
              </>
            ) : (
              <>
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-violet-500/15 text-violet-300">
                  <Camera className="h-6 w-6" strokeWidth={1.5} />
                </span>
                <Button type="button" variant="violet" disabled={!plantCode} onClick={() => scannerRef.current?.open()}>
                  <Camera className="h-4 w-4" strokeWidth={1.75} /> Scannen
                </Button>
                <label
                  className={`text-xs font-medium underline underline-offset-2 ${
                    plantCode ? 'cursor-pointer text-ivory-500 hover:text-ivory-300' : 'cursor-not-allowed text-ivory-700'
                  }`}
                >
                  oder Datei/Foto direkt hochladen (JPG, PNG, PDF)
                  <input
                    type="file"
                    accept="image/jpeg,image/png,application/pdf"
                    capture="environment"
                    className="hidden"
                    disabled={!plantCode || loading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void handleFile(file);
                    }}
                  />
                </label>
              </>
            )}
          </div>

          {error && <p className="text-sm text-danger-400">{error}</p>}
        </CardBody>
      </Card>

      <DocumentScannerModal ref={scannerRef} onCapture={(file) => void handleFile(file)} />
    </div>
  );
}
